import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import api from "@/utils/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Edit, Loader2 } from "lucide-react";
import FieldInspectionForm from "../../../components/district_admin/FieldInspectionForm";

const AirJetLoomView = ({ application, viewPage = false }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-IN");
  };

  const formatCurrency = (amount) => {
    if (!amount) return "-";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };
  const dispatch = useDispatch();
  const nav = useNavigate();
  const [data, setData] = useState();
  const role = useSelector((state) => state.auth.user?.role) || "applicant";
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [adminApproveModalOpen, setAdminApproveModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [sanctionedAmount, setSanctionedAmount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const uploads_array = [
    { name: "Profile Photo", value: "photo" },
    { name: "PAN Copy", value: "pan_copy" },
    { name: "GST Copy", value: "gst_copy" },
    { name: "MSME Certificate", value: "msme_certificate" },
    { name: "Incorporation Certificate", value: "incorporation_certificate" },
    { name: "Udyog Aadhaar Copy", value: "udyog_aadhaar_copy" },
    { name: "Loom Included Copy", value: "loom_included_copy" },
    { name: "Detailed Project Report Copy", value: "dpr_copy" },
    { name: "Loan Sanction Letter", value: "loan_sanction_letter" },
    { name: "Machinery Photo", value: "machinery_photo" },
    { name: "Purchase Proof", value: "purchase_proof" },
    { name: "Origin Certificate", value: "origin_certificate" },
    { name: "Bill of Lading", value: "bill_of_lading" },
    { name: "Bill of Entry", value: "bill_of_entry" },
    { name: "Investment Proof", value: "investment_proof" },
    { name: "Loan Account Copy", value: "loan_account_copy" },
    { name: "Bank Statement", value: "bank_statement" },
    { name: "Scheme Documents", value: "scheme_documents" },
    { name: "Export Oriented Copy", value: "export_oriented_copy" },
    { name: "Opt Out Declaration Copy", value: "opt_out_declaration_copy" },
    { name: "Other", value: "other" },
  ];

  useEffect(() => {
    setData({ ...application });
  }, [application]);

  const getFilePath = (fileType) => {
    if (!data?.files || !Array.isArray(data.files)) return null;
    const file = data.files.find((f) => f.file_type === fileType);
    return file ? file.file_path : null;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      incomplete: {
        variant: "secondary",
        label: "incomplete",
        style: { background: "#fdb3b3", padding: "10px", fontSize: "15px" },
      },
      pending: {
        variant: "secondary",
        label: "Pending",
        style: { padding: "10px", fontSize: "15px" },
      },
      submitted: {
        variant: "default",
        label: "Submitted",
        style: { padding: "10px", fontSize: "15px" },
      },
      approved: {
        variant: "success",
        label: "Approved",
        style: { padding: "10px", fontSize: "15px" },
      },
      rejected: {
        variant: "destructive",
        label: "Rejected",
        style: { padding: "10px", fontSize: "15px" },
      },
      assigned: {
        variant: "outline",
        label: "Assigned",
        style: { padding: "10px", fontSize: "15px" },
      },
      reviewed: {
        variant: "warning",
        label: "Reviewed",
        style: { padding: "10px", fontSize: "15px" },
      },
    };
    const config = statusConfig[status] || {
      variant: "secondary",
      label: status,
    };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const openApproveModal = () => {
    if (role === "admin") {
      setAdminApproveModalOpen(true);
    } else {
      setApproveModalOpen(true);
    }
  };

  const openRejectModal = () => {
    setRejectModalOpen(true);
  };

  const handleApprove = async () => {
    try {
      setIsSubmitting(true);
      let response;
      if (role === "admin") {
        response = await api.post("/admin/approve", {
          form_id: data?.id,
          form_type: "new_airjetlooms",
          sanctioned_amount: sanctionedAmount,
          remarks: remarks,
        });
      } else {
        response = await api.post("/district-admin/approve", {
          form_id: data?.id,
          form_type: "new_airjetlooms",
        });
      }
      setData(response?.data?.data);
      toast({
        title: "Approved",
        description: "Application approved successfully",
        variant: "success",
      });
      setApproveModalOpen(false);
      setAdminApproveModalOpen(false);
      setRemarks("");
      setSanctionedAmount("");
    } catch (error) {
      toast({
        title: "Failed",
        description: error.response?.data?.message || "Approval failed",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    try {
      setIsSubmitting(true);
      const submitData = new FormData();
      submitData.append("remarks", remarks);
      submitData.append("form_id", data?.id);
      submitData.append("form_type", "new_airjetlooms");

      if (role === "admin") {
        await api.post("/admin/reject", submitData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post("/district-admin/reject", submitData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      toast({
        title: "Rejected",
        description: "Application rejected successfully",
        variant: "destructive",
      });
      setRejectModalOpen(false);
      setRemarks("");
    } catch (error) {
      toast({
        title: "Failed",
        description: error.response?.data?.message || "Rejection failed",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFileLink = (filePath, label = "View Document") => {
    if (!filePath || filePath === "null" || filePath === "undefined") {
      return "-";
    }
    return (
      <a
        href={filePath}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline text-sm"
      >
        {label}
      </a>
    );
  };

  const renderArrayField = (arrayData) => {
    if (!arrayData || !Array.isArray(arrayData)) return "-";
    try {
      const parsed =
        typeof arrayData === "string" ? JSON.parse(arrayData) : arrayData;
      return parsed.join(", ");
    } catch {
      return arrayData.join(", ");
    }
  };

  const handleEdit = (tabName) => {
    if (viewPage) {
      nav(`/scheme/new_airjetlooms?tab=${tabName}`);
    } else {
      dispatch(setCurrentTab(tabName));
    }
  };

  return (
    <div className="space-y-6 print:space-y-4">
      {viewPage && (
        <div className="flex items-center justify-between print:hidden">
          <h1 className="text-3xl font-bold">
            Air Jet Loom Application Details
          </h1>
          <div className="flex gap-2">
            <Button asChild variant="default">
              <Link to="/dashboard">Back</Link>
            </Button>
            <Button variant="default" onClick={() => window.print()}>
              Print
            </Button>
          </div>
        </div>
      )}

      {/* Application Header */}
      <Card className="my-5">
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            <span>Purchase of Brand New Air Jet Looms</span>
            {getStatusBadge(data?.status)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 my-5">
            <p>
              <strong>Application No:</strong> {data?.application_no || "-"}
            </p>
            <p>
              <strong>Status:</strong> {data?.status || "-"}
            </p>
            <p>
              <strong>Submitted:</strong> {formatDate(data?.created_at)}
            </p>
            <p>
              <strong>Last Updated:</strong> {formatDate(data?.updated_at)}
            </p>
            {data?.eligibility_points !== undefined && (
              <p>
                <strong>Eligibility Points:</strong> {data?.eligibility_points}
              </p>
            )}
            {data?.eligibility_status && (
              <p>
                <strong>Eligibility Status:</strong> {data?.eligibility_status}
              </p>
            )}
            {data?.priority_points !== undefined && (
              <p>
                <strong>Priority Points:</strong> {data?.priority_points}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Company Details */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            Company Details
          </CardTitle>
          {data?.is_completed == 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEdit("company")}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
            <div>
              <strong>Applicant Name:</strong> {data?.applicant_name || "-"}
            </div>
            <div>
              <strong>Contact Person:</strong> {data?.contact_person || "-"}
            </div>
            <div>
              <strong>Promoters/Directors:</strong> {data?.promoters || "-"}
            </div>
            <div>
              <strong>Company Address:</strong> {data?.company_address || "-"}
            </div>
            <div>
              <strong>City:</strong> {data?.city || "-"}
            </div>
            <div>
              <strong>District:</strong> {data?.district?.name || "-"}
            </div>
            <div>
              <strong>State:</strong> {data?.state || "-"}
            </div>
            <div>
              <strong>Pincode:</strong> {data?.pincode || "-"}
            </div>
            <div>
              <strong>Unit PAN:</strong> {data?.unit_pan || "-"}
            </div>
            <div>
              <strong>GST Number:</strong> {data?.gst_number || "-"}
            </div>
            <div>
              <strong>MSME Type:</strong> {data?.msme_type || "-"}
            </div>
            <div>
              <strong>Existing/New:</strong> {data?.existing_or_new || "-"}
            </div>
            <div>
              <strong>Date of Incorporation:</strong>{" "}
              {formatDate(data?.date_of_incorporation)}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Eligibility Details */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            Eligibility Criteria
          </CardTitle>
          {data?.is_completed == 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEdit("eligibility")}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
            <div>
              <strong>Beneficiary Type:</strong> {data?.beneficiary_type || "-"}
            </div>
            <div>
              <strong>Has Udyog Aadhaar:</strong> {data?.has_udyog_Aadhaar || "-"}
            </div>
            <div>
              <strong>Loom Included in Machinery List:</strong>{" "}
              {data?.loom_included_in_machinery_list || "-"}
            </div>
            <div>
              <strong>Premises Type:</strong> {data?.premises_type || "-"}
            </div>
            <div>
              <strong>Has Other Schemes:</strong>{" "}
              {data?.has_other_schemes || "-"}
            </div>
            <div>
              <strong>Other State Govt Scheme:</strong>{" "}
              {data?.other_state_gov_scheme || "-"}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Products Details */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            Products Details
          </CardTitle>
          {data?.is_completed == 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEdit("products")}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
            <div>
              <strong>Products Manufactured:</strong>{" "}
              {data?.products_manufactured || "-"}
            </div>
            <div>
              <strong>End Use of Products:</strong> {data?.end_use || "-"}
            </div>
            <div>
              <strong>Detailed Project Report Submitted:</strong> {data?.dpr_submitted || "-"}
            </div>
            <div>
              <strong>Bank Loan Sanction:</strong>{" "}
              {data?.bank_loan_sanction || "-"}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Machinery Details */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            Machinery Details
          </CardTitle>
          {data?.is_completed == 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEdit("machinery")}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
            <div>
              <strong>Machinery Type:</strong> {data?.machinery_type || "-"}
            </div>
            <div>
              <strong>Make and Model:</strong> {data?.make_model || "-"}
            </div>
            <div>
              <strong>Serial Number:</strong> {data?.serial_number || "-"}
            </div>
            <div>
              <strong>Basic Cost of Machinery:</strong>{" "}
              {formatCurrency(data?.basic_cost_machinery)}
            </div>
            <div>
              <strong>Basic Cost of Accessories:</strong>{" "}
              {formatCurrency(data?.basic_cost_accessories)}
            </div>
            <div>
              <strong>Custom Duty:</strong> {formatCurrency(data?.custom_duty)}
            </div>
            <div>
              <strong>Country of Origin:</strong>{" "}
              {data?.country_of_origin || "-"}
            </div>
            <div>
              <strong>Purchase Date:</strong> {formatDate(data?.purchase_date)}
            </div>
            <div>
              <strong>Bill of Lading Date:</strong>{" "}
              {formatDate(data?.bill_of_lading_date)}
            </div>
            <div>
              <strong>Bill of Entry Date:</strong>{" "}
              {formatDate(data?.bill_of_entry_date)}
            </div>
            <div>
              <strong>Commissioning Date:</strong>{" "}
              {formatDate(data?.commissioning_date)}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bank Details */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            Bank & Investment Details
          </CardTitle>
          {data?.is_completed == 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEdit("bank")}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
            <div>
              <strong>Total Investment:</strong>{" "}
              {formatCurrency(data?.total_investment)}
            </div>
            <div>
              <strong>Total Loan Applied:</strong>{" "}
              {formatCurrency(data?.total_loan_applied)}
            </div>
            <div>
              <strong>Total Loan Released:</strong>{" "}
              {formatCurrency(data?.total_loan_released)}
            </div>
            <div>
              <strong>Loan Release Date:</strong>{" "}
              {formatDate(data?.loan_release_date)}
            </div>
            <div>
              <strong>Loan Period:</strong> {data?.loan_period || "-"}
            </div>
            <div>
              <strong>Promoters Contribution:</strong>{" "}
              {formatCurrency(data?.promoters_contribution)}
            </div>
            <div>
              <strong>Promoters Contribution %:</strong>{" "}
              {data?.promoters_contribution_percent || "-"}%
            </div>
            <div>
              <strong>Shared with Other Banks:</strong>{" "}
              {data?.shared_with_other_banks || "-"}
            </div>
            <div>
              <strong>Loan Sanction Date:</strong>{" "}
              {formatDate(data?.loan_sanction_date)}
            </div>
            <div>
              <strong>Machinery Type:</strong>{" "}
              {data?.machinery_imported_indigenous || "-"}
            </div>
            <div>
              <strong>Loan Application Date:</strong>{" "}
              {formatDate(data?.loan_application_date)}
            </div>
            <div>
              <strong>Loan Sanction by Authority Date:</strong>{" "}
              {formatDate(data?.loan_sanction_by_authority_date)}
            </div>
            <div>
              <strong>Repayment Start Date:</strong>{" "}
              {formatDate(data?.repayment_start_date)}
            </div>
            <div>
              <strong>Term Loan Account Number:</strong>{" "}
              {data?.term_loan_account_number || "-"}
            </div>
            <div>
              <strong>Interest Rate:</strong> {data?.interest_rate || "-"}%
            </div>
            <div>
              <strong>Interest Charged Per Year:</strong>{" "}
              {formatCurrency(data?.interest_charged_per_year)}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scheme Details */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            Scheme Details
          </CardTitle>
          {data?.is_completed == 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEdit("schemes")}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
            <div>
              <strong>Persons Employed:</strong> {data?.persons_employed || "-"}
            </div>
            {/* <div>
              <strong>ATUF Benefited:</strong> {data?.atuf_benefited || "-"}
            </div> */}
            <div>
              <strong>TN Industrial Policy 2014:</strong>{" "}
              {data?.tn_industrial_policy_2014 || "-"}
            </div>
            <div>
              <strong>TN MSME Policy:</strong> {data?.tn_msme_policy || "-"}
            </div>
            <div>
              <strong>Other Scheme Benefited:</strong>{" "}
              {data?.other_scheme_benefited || "-"}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Priority Details */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            Priority Factors
          </CardTitle>
          {data?.is_completed == 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEdit("priority")}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
            <div>
              <strong>Forward/Backward Integration:</strong>{" "}
              {data?.forward_backward_integration || "-"}
            </div>
            {data?.forward_backward_integration === "Backward" && (
              <>
                <div>
                  <strong>Raw Material Source:</strong>{" "}
                  {data?.backward_raw_material_source || "-"}
                </div>
                <div>
                  <strong>Labour Availability:</strong>{" "}
                  {data?.backward_labour_availability || "-"}
                </div>
                <div>
                  <strong>Looms & Machineries:</strong>{" "}
                  {data?.backward_looms_machineries || "-"}
                </div>
              </>
            )}
            <div>
              <strong>Export Oriented:</strong> {data?.export_oriented || "-"}
            </div>
            <div>
              <strong>Turnover:</strong> {formatCurrency(data?.turnover)}
            </div>
            <div>
              <strong>Cluster Approach:</strong> {data?.cluster_approach || "-"}
            </div>
            <div>
              <strong>Number of Looms in Cluster:</strong>{" "}
              {data?.cluster_approach_no_of_looms || "-"}
            </div>
            <div>
              <strong>Willingness to Switch:</strong>{" "}
              {data?.willingness_to_switch || "-"}
            </div>
            {data?.willingness_to_switch === "yes" && (
              <div className="md:col-span-2">
                <strong>Willingness Description:</strong>{" "}
                {data?.willingness_description || "-"}
              </div>
            )}
            <div>
              <strong>Opt Out from Scheme:</strong>{" "}
              {data?.is_opt_out_from_scheme || "-"}
            </div>
            <div>
              <strong>Entrepreneur Type:</strong>{" "}
              {data?.entrepreneur_type || "-"}
            </div>
            <div>
              <strong>Employment Generation:</strong>{" "}
              {data?.employment_generation || "-"}
            </div>
            <div className="md:col-span-2">
              <strong>Value Added Variety:</strong>{" "}
              {renderArrayField(data?.value_added_variety)}
            </div>
            <div className="md:col-span-2">
              <strong>Cooperative Society Member:</strong>{" "}
              {data?.cooperative_society_member || "-"}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Attachments</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
            <div>
              <strong>Applicant Photo:</strong>{" "}
              {renderFileLink(getFilePath("photo"), "View Photo")}
            </div>
            <div>
              <strong>PAN Copy:</strong>{" "}
              {renderFileLink(getFilePath("pan_copy"))}
            </div>
            <div>
              <strong>GST Copy:</strong>{" "}
              {renderFileLink(getFilePath("gst_copy"))}
            </div>
            <div>
              <strong>MSME Certificate:</strong>{" "}
              {renderFileLink(getFilePath("msme_certificate"))}
            </div>
            <div>
              <strong>Incorporation Certificate:</strong>{" "}
              {renderFileLink(getFilePath("incorporation_certificate"))}
            </div>
            {data?.has_udyog_Aadhaar === "yes" && (
              <div>
                <strong>Udyog Aadhaar Copy:</strong>{" "}
                {renderFileLink(getFilePath("udyog_aadhaar_copy"))}
              </div>
            )}
            {data?.loom_included_in_machinery_list === "yes" && (
              <div>
                <strong>Loom Included Copy:</strong>{" "}
                {renderFileLink(getFilePath("loom_included_copy"))}
              </div>
            )}
            {data?.dpr_submitted === "Yes" && (
              <div>
                <strong>Detailed Project Report Copy:</strong>{" "}
                {renderFileLink(getFilePath("dpr_copy"))}
              </div>
            )}
            {data?.bank_loan_sanction === "Available" && (
              <div>
                <strong>Loan Sanction Letter:</strong>{" "}
                {renderFileLink(getFilePath("loan_sanction_letter"))}
              </div>
            )}
            <div>
              <strong>Machinery Photo:</strong>{" "}
              {renderFileLink(getFilePath("machinery_photo"), "View Photo")}
            </div>
            <div>
              <strong>Purchase Proof:</strong>{" "}
              {renderFileLink(getFilePath("purchase_proof"))}
            </div>
            <div>
              <strong>Origin Certificate:</strong>{" "}
              {renderFileLink(getFilePath("origin_certificate"))}
            </div>
            <div>
              <strong>Bill of Lading:</strong>{" "}
              {renderFileLink(getFilePath("bill_of_lading"))}
            </div>
            <div>
              <strong>Bill of Entry:</strong>{" "}
              {renderFileLink(getFilePath("bill_of_entry"))}
            </div>
            <div>
              <strong>Investment Proof:</strong>{" "}
              {renderFileLink(getFilePath("investment_proof"))}
            </div>
            <div>
              <strong>Loan Account Copy:</strong>{" "}
              {renderFileLink(getFilePath("loan_account_copy"))}
            </div>
            <div>
              <strong>Bank Statement:</strong>{" "}
              {renderFileLink(getFilePath("bank_statement"))}
            </div>
            {data?.other_scheme_benefited === "Yes" && (
              <div>
                <strong>Scheme Documents:</strong>{" "}
                {renderFileLink(getFilePath("scheme_documents"))}
              </div>
            )}
            {data?.export_oriented === "yes" && (
              <div>
                <strong>Export Oriented Copy:</strong>{" "}
                {renderFileLink(getFilePath("export_oriented_copy"))}
              </div>
            )}
            {data?.is_opt_out_from_scheme === "yes" && (
              <div>
                <strong>Opt Out Declaration Copy:</strong>{" "}
                {renderFileLink(getFilePath("opt_out_declaration_copy"))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Declaration */}
      {viewPage && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              Declaration
            </CardTitle>
            {data?.is_completed == 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEdit("declaration")}
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 mt-5">
              <div>
                <strong>Declaration Accepted:</strong>{" "}
                {data?.declaration_accepted ? "Yes" : "No"}
              </div>
              <div>
                <strong>Applicant Signature:</strong>{" "}
                {renderFileLink(
                  getFilePath("applicant_signature"),
                  "View Signature"
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin Remarks Section */}
      {(role === "district_admin" || role === "admin") && (
        <Card>
          <CardHeader>
            <CardTitle>Review Remarks</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.logs?.length > 0 ? (
              <div className="space-y-4">
                {data?.logs.map((log, index) => (
                  <div key={index} className="border p-4 rounded-md">
                    <p>
                      <strong>Remarks:</strong>{" "}
                      {log.remarks || "No remarks provided."}
                    </p>
                    {log.file_path &&
                      log.file_path !== "null" &&
                      log.file_path !== "undefined" && (
                        <p>
                          <strong>File:</strong>{" "}
                          <a
                            href={log.file_path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            View File
                          </a>
                        </p>
                      )}
                    <p>
                      <strong>Reviewed On:</strong>{" "}
                      {log.created_at
                        ? new Date(log.created_at).toLocaleString("en-IN", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                          })
                        : "-"}
                    </p>
                    <p>
                      <strong>Reviewed By:</strong> {log.user?.name || "-"}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p>No remarks provided.</p>
            )}
            {data?.status && data?.status === "submitted" && (
              <FieldInspectionForm
                formType="new_airjetlooms"
                form_id={data?.id}
                setData={setData}
                uploads_array={uploads_array}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Action Buttons for District Admin */}
      {role === "district_admin" && data?.status === "reviewed" && (
        <Card>
          <CardContent className="flex justify-end gap-4 mt-5">
            <Button
              disabled={
                data?.status === "approved" ||
                data?.status === "rejected" ||
                isSubmitting
              }
              variant="outline"
              size="sm"
              onClick={openApproveModal}
            >
              Approve
            </Button>
            <Button
              disabled={
                data?.status === "approved" ||
                data?.status === "rejected" ||
                isSubmitting
              }
              variant="destructive"
              size="sm"
              onClick={openRejectModal}
            >
              Reject
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons for Admin */}
      {role === "admin" &&
        (data?.status === "reviewed" || data?.status === "submitted") && (
          <Card>
            <CardContent className="flex justify-end gap-4 mt-5">
              <Button
                disabled={
                  data?.status === "approved" ||
                  data?.status === "rejected" ||
                  isSubmitting
                }
                variant="outline"
                size="sm"
                onClick={openApproveModal}
              >
                Approve
              </Button>
              <Button
                disabled={
                  data?.status === "approved" ||
                  data?.status === "rejected" ||
                  isSubmitting
                }
                variant="destructive"
                size="sm"
                onClick={openRejectModal}
              >
                Reject
              </Button>
            </CardContent>
          </Card>
        )}

      {/* Approve Modal for District Admin */}
      <Dialog open={approveModalOpen} onOpenChange={setApproveModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Approval</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            Are you sure you want to approve this Air Jet Loom application?
          </div>
          <DialogFooter className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => setApproveModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Approving...
                </>
              ) : (
                "Approve"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Application</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-2">
            <p>Enter remarks for rejection:</p>
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter rejection remarks..."
              rows={4}
            />
          </div>
          <DialogFooter className="flex space-x-2">
            <Button variant="outline" onClick={() => setRejectModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              disabled={isSubmitting || !remarks.trim()}
              variant="destructive"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Rejecting...
                </>
              ) : (
                "Reject"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Modal for Admin */}
      <Dialog
        open={adminApproveModalOpen}
        onOpenChange={setAdminApproveModalOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Application</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Sanctioned Amount:</label>
              <Input
                type="number"
                value={sanctionedAmount}
                onChange={(e) => setSanctionedAmount(e.target.value)}
                placeholder="Enter sanctioned amount in INR"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Remarks:</label>
              <Textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Enter approval remarks..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => setAdminApproveModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApprove}
              disabled={
                isSubmitting || !remarks.trim() || !sanctionedAmount.trim()
              }
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Approving...
                </>
              ) : (
                "Approve"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AirJetLoomView;
